import { AbstractPmsApiService } from './abstract.service';
import { ReservationTransaction } from '../models';

/**
 * @class ReservationTransactionPmsApiService
 * @abstract
 * @extends {AbstractPmsApiService<ReservationTransaction>}
 */
export class ReservationTransactionPmsApiService extends AbstractPmsApiService {

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property/:propertyId/reservation(/:reservationId)/folio(/:folioId)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {ReservationTransaction}
     * @protected
     */
    newModel(attributes, ...params) {

        return new ReservationTransaction(attributes);
    }

    newTransactionCollection(paginationData, ...params) {

        return {
            reservation: paginationData.reservation,
            total_row: paginationData.total_row,
            models: Object.values(paginationData.data).map(groups => {

                return {
                    'total_row': groups.total_row,
                    'items': groups.items.map(attributes => this.newModel(attributes, ...params))
                };
            })
        };
    }

    /**
     * @param {...*} params
     * @return {function(response: Object): PaginationCollection<M>}
     * @protected
     */
    getTransactionCollector(...params) {

        return (response) => {

            const responseData = this.extractData(response);

            if (responseData.data && Array.isArray(responseData.data)) {

                responseData.data = {
                    group: {
                        items: responseData.data
                    }
                };
            }

            return this.newTransactionCollection(responseData, ...params);
        };
    }

    /**
     * @param {Object=} params
     * @param {Reservation=} reservation
     * @returns {Promise.<PaginationCollection<ReservationTransaction>>}
     */
    get (params = {}, reservation = null) {

        const url = this.url({
            propertyId: reservation ? reservation.property_id : 0,
            reservationId: reservation ? reservation.id : 0,
        });

        return this.http
            .post(url, params, {
                headers: this.authHeaders,
            })
            .then(this.getTransactionCollector())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {ReservationTransaction} transaction
     * @param {Reservation} reservation
     * @returns {Promise.<ReservationTransaction>}
     */
    create(transaction, reservation) {

        const url = this.url({reservationId: reservation.id});

        return this.http
            .post(url, transaction.toObjectExcept('reservation'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {ReservationTransaction} transaction
     * @returns {Promise.<ReservationTransaction>}
     */
    update(transaction) {

        const url = this.url({
            reservationId: transaction.reservation.id,
            id:            transaction.id
        });

        return this.http
            .put(url, transaction.toObjectExcept('reservation'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {ReservationTransaction} transaction
     * @param reservation
     * @returns {Promise.<boolean>}
     */
    delete(transaction, reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            reservationId: reservation.id,
            folioId: transaction.id,
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Object} payment
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    addPayment(payment, reservation) {

        return this.addTransaction(payment, reservation, 'add-payment');
    }

    /**
     * @param {Object} security deposit payment
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    addSecurityDepositPayment(payment, reservation) {

        return this.addTransaction(payment, reservation, 'add-security-deposit-payment');
    }

    /**
     * @param {Object} security deposit payment
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    refundDeposit(payment, reservation) {

        return this.addTransaction(payment, reservation, 'add-security-deposit-refund');
    }

    /**
     * @param {Object} payment
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    refundPayment(payment, reservation) {

        return this.addTransaction(payment, reservation, 'refund-payment');
    }

    //update-payment/33965
    updatePayment(payment, reservation) {
        const path = `update-payment/${payment.id}`;

        return this.editTransaction(payment, reservation, path);
    }

    //update-refund/33965
    updateRefund(payment, reservation) {
        const path = `update-refund/${payment.id}`;

        return this.editTransaction(payment, reservation, path);
    }
    
    //update-security-deposit-refund/33564
    /**
     * @param {Object} payment
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    updateSecurityDepositRefund(payment, reservation) {
        const path = `update-security-deposit-refund/${payment.id}`;

        return this.editTransaction(payment, reservation, path);
    }

    //update-security-deposit-payment/124422
    /**
     * @param {Object} payment
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    updateSecurityDepositPayment(payment, reservation) {
        const path = `update-security-deposit-payment/${payment.id}`;

        return this.editTransaction(payment, reservation, path);
    }

    /**
     * @param {Object} transaction
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    addItem(transaction, reservation) {

        transaction.item_id = transaction.item.id;
        delete transaction.item;

        return this.addTransaction(transaction, reservation, 'add-item');
    }

    addTaxFee(transaction, reservation) {

        transaction.tax_fee_id = transaction.tax_fee.id;
        delete transaction.tax_fee;

        return this.addTransaction(transaction, reservation, 'add-tax-fee');
    }

    addRoomRevenue(transaction, reservation) {

        transaction.taxes_fees = transaction.taxes_fees.map(({id}) => id);

        return this.addTransaction(transaction, reservation, 'add-room-revenue');
    }

    /**
     * @param {Object} transaction
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    adjustCharge(transaction, reservation) {

        return this.addTransaction(transaction, reservation, 'adjust-charge');
    }

    addTransaction(transaction, reservation, path = null) {

        const urlParams = {
            propertyId: reservation.property_id,
            reservationId: reservation.id,
        };

        if (path) {

            urlParams['folioId'] = path;
        }

        transaction.reservation_accommodation_id = transaction.assign_to.id;
        transaction.room_id = transaction.assign_to.room.id || null;
        transaction.guest_id = transaction.assign_to.guest.id || null;
        delete transaction.assign_to;

        const url = this.url(urlParams);

        return this.http
            .post(url, transaction, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    editTransaction(transaction, reservation, path = null) {

        const urlParams = {
            propertyId: reservation.property_id,
            reservationId: reservation.id,
        };

        if (path) {

            urlParams['folioId'] = path;
        }

        const url = this.url(urlParams);

        return this.http
            .put(url, transaction, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    defaultTaxFee(reservation) {

        const urlParams = {
            propertyId: reservation.property_id,
            reservationId: reservation.id,
            folioId: 'default-taxes-fees',
        };

        const url = this.url(urlParams);

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    suggested(reservation) {

        const urlParams = {
            propertyId: reservation.property_id,
            reservationId: reservation.id,
            folioId: 'suggested',
        };

        const url = this.url(urlParams);

        const adjustmentTypeMapper = {
            'App\\Item': 'item',
            'App\\Addon': 'item',
            'App\\TaxFee': 'tax-fee',
            'room-revenue': 'room-revenue',
        };

        const mapSuggested = models => models.map(({adjustment_id, adjustment_type}) => {

            return {
                id: adjustment_id,
                adjustment_type: adjustmentTypeMapper[adjustment_type]
            };
        });

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .then(mapSuggested)
            .catch(this.getErrorHandler());
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/reservation-transaction.service.js